import { useCallback, useState } from 'react';
import { PaginationState, RowSelectionState, Updater } from '@tanstack/react-table';

import { isFunction } from '@xemplo/common-util';

import { DEFAULT_PAGINATION } from './server-side-state.constants';
import { ServerSideRowSelectionState } from './server-side-state.types';

/**
 * This hook is used to manage the state of a table that is using server side paging.
 * It includes the row selection state and the pagination state.
 * It provides a functionality similar to what gmail does.
 */
export const useServerSideState = (defaultPagination = DEFAULT_PAGINATION) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>(defaultPagination);
  const [overallRowSelection, setOverallRowSelection] =
    useState<ServerSideRowSelectionState>({});

  const resetPagination = useCallback(() => {
    setPagination(defaultPagination);
  }, [defaultPagination]);

  /**
   * Updates the row selection state and stores it in the overallRowSelection state.
   * This is to avoid losing the selection when navigating to a different page.
   */
  const onRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      setRowSelection((prev) => {
        const state = isFunction(updater) ? updater(prev) : updater;
        setOverallRowSelection((prevOverall) => ({
          ...prevOverall,
          [pagination.pageIndex]: {
            pagination,
            rowSelection: state,
          },
        }));
        return state;
      });
    },
    [pagination]
  );

  /**
   * Updates the pagination state and restores the row selection state if it exists.
   * If it doesn't exist, it creates an empty row selection state.
   * This is to avoid having selected rows when navigating to a different page.
   * It also restore the selection when a previously visited page is visited again and contains
   * row selection state.
   */
  const onPaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      setPagination((prev) => {
        const state = isFunction(updater) ? updater(prev) : updater;

        const existingSelection = overallRowSelection[state.pageIndex];
        if (existingSelection) {
          const { rowSelection } = existingSelection;
          setRowSelection(rowSelection);
        } else {
          const pageItems = Array.from({ length: state.pageSize });
          setRowSelection(
            Object.fromEntries(pageItems.map((_, index) => [index, false]))
          );
        }

        return state;
      });
    },
    [overallRowSelection]
  );

  return {
    onRowSelectionChange,
    onPaginationChange,
    rowSelection,
    pagination,
    resetPagination,
  };
};
